import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ID, withTransaction } from '@datorama/akita';
import { Subject } from 'rxjs';
import {
    BaseTimeSeriesData,
    generateTimeSeriesDataId,
    LegacyMarketData
} from './time-series-data.model';
import { TimeSeriesDataStore } from './time-series-data.store';
import { AppConfigQuery } from '../../../core/state/app-config/app-config.query';
import { tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class TimeSeriesDataService {
    // Subject to signal user-initiated Add events
    private userInitiatedAddSubject = new Subject<BaseTimeSeriesData<LegacyMarketData>>();
    public userInitiatedAdd$ = this.userInitiatedAddSubject.asObservable();

    constructor(
        private timeSeriesDataStore: TimeSeriesDataStore,
        private http: HttpClient,
        private appConfig: AppConfigQuery
    ) {}

    get(instrumentIds: Array<number>) {
        return this.http
            .get<BaseTimeSeriesData[]>(
                `${this.appConfig.get().gatewayBaseUrl}/marketdata/instrument-value`,
                {
                    params: {
                        instrumentId: instrumentIds.join(',')
                    }
                }
            )
            .pipe(
                tap((entities) => {
                    // This upsertMany is triggered by your GET (e.g. on navigation)
                    this.timeSeriesDataStore.upsertMany(entities);
                })
            );
    }

    addLegacyMarketValue<T = LegacyMarketData>(timeSeriesData: BaseTimeSeriesData<T>) {
        return this.http
            .post<LegacyMarketData>(
                `${this.appConfig.get().gatewayBaseUrl}/marketdata/instrument-value`,
                timeSeriesData
            )
            .pipe(
                withTransaction((res) => {
                    const id = generateTimeSeriesDataId(
                        res.instrumentId,
                        new Date(res.date)
                    );
                    this.timeSeriesDataStore.upsert(
                        id,
                        res as BaseTimeSeriesData<LegacyMarketData>
                    );
                    // Emit a dedicated user-initiated event
                    this.userInitiatedAddSubject.next(
                        res as BaseTimeSeriesData<LegacyMarketData>
                    );
                    this.timeSeriesDataStore.update((state) => ({
                        ...state,
                        ui: { lastEntryByUser: new Date() }
                    }));
                })
            );
    }

    recalculateNav(instrumentId: String) {
        return this.http
            .patch(
                `${
                    this.appConfig.get().gatewayBaseUrl
                }/transactions/private-fund/${instrumentId}/recalculatePrelNav`,
                null
            )
            .pipe();
    }

    update(id, timeSeriesData: Partial<BaseTimeSeriesData>) {
        this.timeSeriesDataStore.update(id, timeSeriesData);
    }

    remove(id: ID) {
        this.timeSeriesDataStore.remove(id);
    }
}
